import {createAuthClient} from "better-auth/react"
import {kepAuthClient} from "auth/client"
import {inferAdditionalFields,} from "better-auth/client/plugins"
import type { Role } from "shared"

// better-auth is mounted on otk-be at its default `/api/auth` basePath and
// proxied from vite (see vite.config.ts) so the session cookie stays
// first-party. Calling its native endpoints directly instead of wrapping
// them behind our own routes. No `baseURL` override — better-auth defaults
// to the relative `/api/auth`, same-origin in both dev (via the vite proxy)
// and production (otk-be serves the built UI itself).
//
// `inferAdditionalFields` mirrors `otkUserAuthConfig.additionalFields`
// (otk-be's `src/auth-utils.ts`) by hand rather than importing it —
// otk-be is server-only code, not a shared package — so `data.user` is
// typed with `rnokpp`/`subjectId` instead of just better-auth's core
// fields. The server already returns them on the wire either way; this only
// makes the client's types (and therefore this file) honest about it.
export const authClient = createAuthClient({
  plugins: [
    kepAuthClient(),
    inferAdditionalFields({
      user: {
        rnokpp: { type: "string" },
        subjectId: { type: "string" },
        roles: { type: "json" },
      },
    }),
  ],
})

export interface AuthSession {
  user: {
    id: string
    name: string
    email: string
    rnokpp: string
    subjectId: string | null
    roles: Role[]
  }
}

export async function createKepChallenge(): Promise<string> {
  const { data, error } = await authClient.kep.challenge()
  if (error) throw error
  return data.challenge
}

export async function verifyKep(
  kepVerification: string
): Promise<AuthSession> {
  const { data, error } = await authClient.kep.verify({ kepVerification })
  if (error) throw error
  return {
    user: {
      id: data.user.id,
      name: data.user.name,
      email: data.user.email,
      rnokpp: data.user.rnokpp,
      subjectId: data.user.subjectId ?? null,
      roles: (data.user.roles as Role[] | undefined) ?? [],
    },
  }
}
